'use client';

import { Edit2, Check, ChevronDown, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/shared/DataTable';
import type { DataTableTheme } from '@/components/shared/DataTable/types';
import {
  findStandardField,
  isBlockingCsvAttributeTokenCollision,
  isPeopleListWithoutContactMethod,
  type CsvAttributeTokenCollision,
  type CsvFieldMapping,
} from '@/utils/csvAiMapping';

interface ExistingAudienceInfo {
  name: string;
  rowCount?: number;
}

interface ReviewStepProps {
  csvHeaders: string[];
  csvSampleRows: string[][];
  editedMappings: Record<string, string>;
  aiMapping: CsvFieldMapping;
  standardFields: readonly string[];
  isEditingMappings: boolean;
  error: string | null;
  attributeTokenCollisions: CsvAttributeTokenCollision[];
  /** What `applyCsvMapping` will actually produce for the current mappings, or null when unknown. */
  predictedImport: { type: 'person' | 'company'; data: Record<string, unknown>[] } | null;
  existingAudience?: ExistingAudienceInfo;
  csvPreviewTheme: Partial<DataTableTheme>;
  onMappingChange: (column: string, field: string) => void;
  onStartEditing: () => void;
  onSaveEdits: () => void;
  onCancelEdits: () => void;
}

export function ReviewStep({
  csvHeaders,
  csvSampleRows,
  editedMappings,
  aiMapping,
  standardFields,
  isEditingMappings,
  error,
  attributeTokenCollisions,
  predictedImport,
  existingAudience,
  csvPreviewTheme,
  onMappingChange,
  onStartEditing,
  onSaveEdits,
  onCancelEdits,
}: ReviewStepProps) {
  const blockingCollisions = attributeTokenCollisions.filter(
    isBlockingCsvAttributeTokenCollision,
  );
  const warningCollisions = attributeTokenCollisions.filter(
    (collision) => !isBlockingCsvAttributeTokenCollision(collision),
  );
  const peopleListWithoutContactMethod = isPeopleListWithoutContactMethod(predictedImport);

  return (
    <div className="space-y-5">
      {/* Append banner */}
      {existingAudience && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-900 font-medium">
            Adding to &ldquo;{existingAudience.name}&rdquo;
            {typeof existingAudience.rowCount === 'number' && (
              <span className="font-normal text-blue-800">
                {' '}— currently {existingAudience.rowCount.toLocaleString()} row
                {existingAudience.rowCount === 1 ? '' : 's'}
              </span>
            )}
          </p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {peopleListWithoutContactMethod && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-amber-700" />
            <div>
              <p className="text-sm font-medium text-amber-900">
                Nobody in this file is reachable yet
              </p>
              <p className="mt-1 text-xs text-amber-800">
                Not one row carries an email, phone number or LinkedIn URL. The people still
                import — with their names, titles and companies — but nothing can be sent to
                them until a contact is found.
              </p>
              <p className="mt-1 text-xs text-amber-800">
                Open the audience and use <span className="font-medium">Find contacts</span> on the
                Email column to fill the missing addresses from each person&rsquo;s name and company.
              </p>
            </div>
          </div>
        </div>
      )}

      {blockingCollisions.length > 0 && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-red-700" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900">
                Fix duplicate custom-attribute tokens before importing
              </p>
              <ul className="mt-1 space-y-1 text-xs text-red-800">
                {blockingCollisions.map((collision) => (
                  <li key={`${collision.kind}:${collision.token}`}>
                    {collision.columns.map((column) => `“${column}”`).join(' and ')} would both use{' '}
                    <code>{`{{${collision.token}}}`}</code>, so one custom value would silently
                    hide the other.
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-red-800">
                Rename one CSV header, or map or skip one of the colliding columns.
              </p>
              {!isEditingMappings && (
                <button
                  type="button"
                  onClick={onStartEditing}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-red-700 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-red-800"
                >
                  <Edit2 size={13} />
                  Fix mappings
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {warningCollisions.length > 0 && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-amber-700" />
            <div>
              <p className="text-sm font-medium text-amber-900">
                Custom attribute token warning
              </p>
              <ul className="mt-1 space-y-1 text-xs text-amber-800">
                {warningCollisions.map((collision) => (
                  <li key={`${collision.kind}:${collision.token}`}>
                    {collision.columns.map((column) => `“${column}”`).join(' and ')} also resolves
                    to <code>{`{{${collision.token}}}`}</code>. Import is allowed; when a canonical
                    or system value exists, it takes precedence in templates.
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Field Mappings — shows ALL columns, mapped and unmapped */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Field Mappings</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {isEditingMappings
                ? 'Map your CSV columns to standard fields'
                : 'How your CSV columns map to standard fields'}
            </p>
          </div>
          {!isEditingMappings ? (
            <button
              type="button"
              onClick={onStartEditing}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <Edit2 size={14} />
              Edit Mappings
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onCancelEdits}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={onSaveEdits}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                <Check size={14} />
                Save
              </button>
            </div>
          )}
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {/* Table header */}
          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
            <div className="grid grid-cols-[1fr_20px_200px] gap-3 items-center">
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Your CSV Column
              </span>
              <span />
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Maps To
              </span>
            </div>
          </div>

          {/* All CSV headers — mapped + ignored */}
          <div className="divide-y divide-gray-100 max-h-[260px] overflow-y-auto">
            {csvHeaders.map((csvColumn) => {
              const mappedField = editedMappings[csvColumn];
              const isIgnored = !mappedField || mappedField === '__SKIP__';
              const exampleValue = csvSampleRows[0]?.[csvHeaders.indexOf(csvColumn)];
              const columnCollisions = attributeTokenCollisions.filter(
                (collision) => collision.columns.includes(csvColumn),
              );
              const hasBlockingCollision = columnCollisions.some(
                isBlockingCsvAttributeTokenCollision,
              );
              // Fields already claimed by other columns — hide them from this column's options
              // to prevent two columns from silently mapping to the same standard field.
              // 'attributes' is excluded: it is a free-form JSONB bag that explicitly allows
              // multiple columns to map to it (each column name becomes a key).
              const claimedByOthers = new Set(
                Object.entries(editedMappings)
                  .filter(([col, f]) => col !== csvColumn && f && f !== '__SKIP__' && f !== 'attributes')
                  .map(([, f]) => f),
              );

              return (
                <div
                  key={csvColumn}
                  className={cn(
                    'px-4 py-3 hover:bg-gray-50/60 transition-colors',
                    isIgnored && !isEditingMappings && 'opacity-45',
                    hasBlockingCollision && 'bg-red-50/70',
                    !hasBlockingCollision && columnCollisions.length > 0 && 'bg-amber-50/70',
                  )}
                >
                  <div className="grid grid-cols-[1fr_20px_200px] gap-3 items-center">
                    {/* Column name + example */}
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {csvColumn}
                      </span>
                      {exampleValue && (
                        <span className="text-xs text-gray-400 truncate hidden sm:block">
                          e.g. &ldquo;{exampleValue}&rdquo;
                        </span>
                      )}
                      {columnCollisions.length > 0 && (
                        <AlertTriangle
                          size={14}
                          className={cn(
                            'flex-shrink-0',
                            hasBlockingCollision ? 'text-red-600' : 'text-amber-600',
                          )}
                          aria-label={`Template token collision: ${columnCollisions
                            .map((collision) => `{{${collision.token}}}`)
                            .join(', ')}`}
                        />
                      )}
                    </div>

                    <span className="text-gray-300 text-sm text-center">→</span>

                    {/* Mapping — select in edit mode, badge in view mode */}
                    {isEditingMappings ? (
                      <Select
                        value={mappedField || '__SKIP__'}
                        onValueChange={(value) => onMappingChange(csvColumn, value)}
                      >
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue placeholder="Skip this field" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__SKIP__">
                            <span className="text-gray-400 italic">Skip this field</span>
                          </SelectItem>
                          {standardFields
                            .filter((field) => !claimedByOthers.has(field))
                            .map((field) => (
                              <SelectItem key={field} value={field}>
                                {field === 'attributes' ? (
                                  <span className="text-violet-700 font-medium">
                                    Custom attribute (free-form)
                                  </span>
                                ) : (
                                  <span className="capitalize">{field.replace(/_/g, ' ')}</span>
                                )}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="h-8 flex items-center px-3 border border-gray-200 rounded-md bg-gray-50">
                        {!isIgnored ? (
                          mappedField === 'attributes' ? (
                            <span className="text-xs text-violet-700 font-medium">
                              Custom attribute
                            </span>
                          ) : (
                            <span className="text-sm capitalize">{mappedField.replace(/_/g, ' ')}</span>
                          )
                        ) : (
                          <span className="text-xs text-gray-400 italic">Will be ignored</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Data Preview */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-gray-900">Data Preview</h3>
        <DataTable
          data={csvSampleRows.slice(0, 10).map((row, idx) => {
            const rowData: Record<string, string | number> = { _id: idx };
            csvHeaders.forEach((_, hi) => {
              rowData[`col_${hi}`] = row[hi] || '—';
            });
            return rowData;
          })}
          columns={csvHeaders.map((header, hi) => {
            const mapped = findStandardField(header, editedMappings);
            const isMapped = mapped && mapped !== '__SKIP__';
            // For attributes-mapped columns the original header is the JSONB key, so
            // label with the header itself rather than the generic "attributes".
            const label = !isMapped
              ? `(${header})`
              : mapped === 'attributes'
                ? header
                : mapped.replace(/_/g, ' ');
            return {
              id: `col_${hi}`,
              label,
              accessor: `col_${hi}`,
              type: 'text' as const,
              sortable: false,
            };
          })}
          rowKey="_id"
          density="compact"
          stickyHeader={false}
          stickyActions={false}
          pagination={{ mode: 'client', pageSize: 10, total: 10 }}
          theme={csvPreviewTheme}
        />
        <p className="text-xs text-gray-400 text-center">
          Showing first {Math.min(csvSampleRows.length, 10)} rows
        </p>
      </div>

      {/* AI summary — collapsed by default to reduce clutter */}
      <details className="group">
        <summary className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none list-none w-fit">
          <ChevronDown
            size={12}
            className="transition-transform duration-150 group-open:rotate-180 flex-shrink-0"
          />
          AI detected{' '}
          <span className="font-medium text-gray-700 capitalize">{aiMapping.detected_type}</span>{' '}
          data &nbsp;·&nbsp; {Math.round(aiMapping.confidence * 100)}% confidence
        </summary>
        {aiMapping.reasoning && (
          <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-xs text-gray-600">{aiMapping.reasoning}</p>
          </div>
        )}
      </details>
    </div>
  );
}
